 /**
 * AdrSearch — Hybrid Collection JS
 * =================================
 * STRATEGY:
 *   • Page 1, no filters → Shopify/Liquid already rendered the grid. Do nothing.
 *   • User changes filter OR sort → API takes over, replaces grid content.
 *   • User paginates (page 2+) → API takes over.
 *   • Once API mode is active, ALL subsequent renders come from the API.
 *
 * Upload to: Shopify Admin → Online Store → Themes → Assets
 * Filename:  adrsearch-collection.js
 */
(function () {
  'use strict';

  // ── Configuration ────────────────────────────────────────────────────────────
  const cfg = {
    handle:       (window.AdrSearch && window.AdrSearch.collection)    || '',
    apiBase:      (window.AdrSearch && 'https://apitest.rug.tv/apps')       || '/apps/adrsearch',
    pageSize:     (window.AdrSearch && window.AdrSearch.pageSize)      || 24,
    imageRatio:      (window.AdrSearch && window.AdrSearch.imageRatio)      || 'adapt',
    cardColorScheme: (window.AdrSearch && window.AdrSearch.cardColorScheme)   || 'scheme-2',
    quickAdd:        (window.AdrSearch && window.AdrSearch.quickAdd)          || 'standard',
    ads:             (window.AdrSearch && window.AdrSearch.ads)               || [],
    store:           (window.AdrSearch && window.AdrSearch.store)             || '',
    useAdrSearch:    !!(window.AdrSearch && window.AdrSearch.useAdrSearch === true),
  };

  // ── State ────────────────────────────────────────────────────────────────────
  const state = {
    apiMode:  false,   // FALSE = Shopify rendered; TRUE = API has taken over
    page:     1,
    limit:    cfg.pageSize,
    sort:     '',
    filters:  { 'availability': ['in_stock'] },  // default: In Stock checked
    query:    '',      // current search query — empty means collection browse
    total:    parseInt((window.AdrSearch && window.AdrSearch.shopifyTotal) || '0', 10),
    loading:  false,
  };

  // ── DOM refs ─────────────────────────────────────────────────────────────────
  // Use ES5 getters so each property does a fresh getElementById at call time.
  // This means it doesn't matter whether the script tag is above or below the
  // HTML elements — nothing is null just because it was looked up too early.
  const els = {
    get grid()             { return document.getElementById('product-grid'); },
    get loading()          { return document.getElementById('adrsearch-loading'); },
    get empty()            { return document.getElementById('adrsearch-empty'); },
    get pagination()       { return document.getElementById('adrsearch-pagination'); },
    get count()            { return document.getElementById('adrsearch-count'); },
    get sort()             { return document.getElementById('adrsearch-sort'); },
    get facetsInner()      { return document.getElementById('adrsearch-facets-inner'); },
    get activeFilters()    { return document.getElementById('adrsearch-active-filters'); },
    get clearBtn()         { return document.getElementById('adrsearch-clear-filters'); },
    get sidebar()          { return document.getElementById('adrsearch-sidebar'); },
    get overlay()          { return document.getElementById('adrsearch-overlay'); },
    get mobileFilterBtn()  { return document.getElementById('adrsearch-mobile-filter-btn'); },
    get sidebarClose()     { return document.getElementById('adrsearch-sidebar-close'); },
    get shopifyPagination(){ return document.getElementById('adrsearch-shopify-pagination'); },
    get gridContainer()    { return document.querySelector('.collection__products'); },
    get paginationEl()     { return document.getElementById('adrsearch-pagination'); },
    get searchInput()      { return document.getElementById('adrsearch-q'); },
    get searchClear()      { return document.getElementById('adrsearch-clear'); },
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // URL STATE — push/read filter+sort state in the browser URL
  // Format: ?sort=price_asc&filter.custom.color=Beige&filter.custom.material=Wool
  // ═══════════════════════════════════════════════════════════════════════════

  /** Write current state.filters + state.sort into the URL (no page reload). */
  function pushUrl() {
    var params = new URLSearchParams();

    if (state.query) params.set('q', state.query);
    if (state.sort)  params.set('sort', state.sort);

    Object.keys(state.filters).forEach(function (key) {
      var val = state.filters[key];

      if (key === 'availability') return;  // no pill for in_stock / out_of_stock

      if (key === 'price' && typeof val === 'object') {
        if (val.min) params.set('filter.price.min', val.min);
        if (val.max) params.set('filter.price.max', val.max);
        return;
      }

      if (Array.isArray(val)) {
        val.forEach(function (v) {
          params.append('filter.' + key, v);
        });
      }
    });

    var qs      = params.toString();
    var newUrl  = window.location.pathname + (qs ? '?' + qs : '');
    history.pushState({ adrsFilters: state.filters, adrsSort: state.sort }, '', newUrl);
  }

  /** Read URL params and restore state.filters + state.sort. */
  function readUrl() {
    var params  = new URLSearchParams(window.location.search);
    var filters = {};

    params.forEach(function (value, key) {
      if (key === 'q') {
        state.query = value;
        if (els.searchInput) {
          els.searchInput.value = value;
          if (els.searchClear) els.searchClear.style.display = value ? '' : 'none';
        }
        return;
      }
      if (key === 'sort') {
        state.sort = value;
        if (els.sort) els.sort.value = value;
        return;
      }

      if (key === 'filter.price.min') {
        if (!filters['price']) filters['price'] = {};
        filters['price'].min = parseFloat(value);
        return;
      }
      if (key === 'filter.price.max') {
        if (!filters['price']) filters['price'] = {};
        filters['price'].max = parseFloat(value);
        return;
      }

      // filter.custom.color=Beige  →  filters['custom.color'] = ['Beige', ...]
      if (key.startsWith('filter.')) {
        var facetKey = key.slice('filter.'.length);
        if (!filters[facetKey]) filters[facetKey] = [];
        filters[facetKey].push(value);
      }
    });

    return filters;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDOFF LOGIC — the heart of the hybrid strategy
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Called the FIRST TIME a user interacts (filter change or sort change).
   * From this point on, all product rendering comes from the MariaDB API.
   * The Shopify-rendered grid is replaced with API results.
   */
  function activateApiMode() {
    if (!state.apiMode) {
      state.apiMode = true;
      if (els.shopifyPagination) els.shopifyPagination.style.display = 'none';
      console.log('[AdrSearch] API mode activated from:', new Error().stack.split('\n')[2]);
    }

    // Reset to page 1 and replace grid for new filter/sort
    state.page = 1;
    if (els.grid) els.grid.innerHTML = '';
    pushUrl();
    loadProducts();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // API FETCH — Products
  // ═══════════════════════════════════════════════════════════════════════════

  async function loadProducts() {
    // Don't guard with state.loading — it blocks new queries while previous is in-flight
    // Instead just show loading indicator and let the latest request win
    state.loading = true;
    showLoading(true);

    const params = new URLSearchParams({ page: state.page, limit: state.limit });
    if (state.sort)   params.set('sort',    state.sort);
    if (cfg.store)    params.set('store',   cfg.store);
    const encoded = encodeFilters(state.filters);
    if (encoded)      params.set('filters', encoded);

    // If on a collection page with a query → search within collection
    // If on /collections/all or no handle → global search
    var url;
    if (state.query && cfg.handle && cfg.handle !== 'all') {
      // Search within collection — passes both handle and q to collection endpoint
      params.set('q', state.query);
      url = cfg.apiBase + '/collection/' + cfg.handle + '?' + params.toString();
    } else if (state.query) {
      // Global search — no specific collection
      url = cfg.apiBase + '/search?q=' + encodeURIComponent(state.query) + '&' + params.toString();
    } else {
      // Normal collection browse — no query
      url = cfg.apiBase + '/collection/' + cfg.handle + '?' + params.toString();
    }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();

      state.total = data.total || data.products.length;

      if (!data.products || data.products.length === 0) {
        showGrid(false);
        showEmpty(true);
        updateCount(0);
        renderPagination(state.page, state.limit, state.total);
        return;
      }

      renderGrid(data.products);
      updateCount(state.total);
      showEmpty(false);
      showGrid(true);
      renderPagination(state.page, state.limit, state.total);
      // Hide Shopify native pagination now that AdrSearch has loaded products
      var shopifyPag = document.getElementById('adrsearch-shopify-pagination');
      if (shopifyPag) shopifyPag.style.display = 'none';
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('[AdrSearch] Product fetch failed:', err);
      showGrid(true);
      if (els.grid) {
        els.grid.innerHTML =
          '<li class="grid__item" style="grid-column:1/-1;text-align:center;padding:40px;color:#c0392b">' +
          'Unable to load products — please refresh the page.' +
          '</li>';
      }
    } finally {
      state.loading = false;
      showLoading(false);
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // API FETCH — Facets (always runs, non-blocking, sidebar only)
  // ═══════════════════════════════════════════════════════════════════════════

  async function loadFacets() {
    // Global only when no handle or on /all — collection+query = search within collection
    var isGlobal = !cfg.handle || cfg.handle === 'all';
    var storeParam = cfg.store ? '&store=' + encodeURIComponent(cfg.store) : '';

    // Build facets URL — always pass q and filters so counts reflect current search/filters
    var facetParams = new URLSearchParams();
    if (cfg.store)    facetParams.set('store',      cfg.store);
    if (state.query)  facetParams.set('q',           state.query);
    // Always pass collection when on a collection page — even with a query
    if (!isGlobal)    facetParams.set('collection',  cfg.handle);

    // Pass current filters (except availability — handled server-side separately)
    // Pass all filters including availability so facet counts match product counts
    var facetFilters = Object.assign({}, state.filters);
    var encodedFilters = encodeFilters(facetFilters);
    if (encodedFilters) facetParams.set('filters', encodedFilters);

    var url = cfg.apiBase + '/facets?' + facetParams.toString();

    try {
      const res  = await fetch(url);
      if (!res.ok) {
        console.error('[AdrSearch] Facet API returned HTTP ' + res.status + ' for: ' + url);
        if (els.facetsInner) els.facetsInner.innerHTML = '';
        return;
      }
      const data = await res.json();
      console.log('[AdrSearch] Facets loaded:', (data.facets || []).length, 'facets, price:', data.price);
      renderFacets(data.facets || [], data.price || {});
    } catch (err) {
      console.error('[AdrSearch] Facet fetch failed:', err, 'URL:', url);
      if (els.facetsInner) els.facetsInner.innerHTML = '';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERING
  // ═══════════════════════════════════════════════════════════════════════════

  function renderGrid(products) {
    if (!els.grid) return;

    // Inject ad cards at configured positions
    var ads = cfg.ads || [];
    var html = '';
    for (var pi = 0; pi < products.length; pi++) {
      html += renderCard(products[pi]);
      var pos = pi + 1; // 1-based position
      for (var ai = 0; ai < ads.length; ai++) {
        var ad = ads[ai];
        if (!ad || !ad.position || !ad.image) continue;
        var isMatch = ad.repeat
          ? (pos % ad.position === 0)
          : (pos === ad.position);
        if (isMatch) html += renderAdCard(ad);
      }
    }
    els.grid.innerHTML = html;
  }

  function renderCard(p) {
    return '<li class="grid__item">' + renderCardInner(p) + '</li>';
  }

  function renderAdCard(ad) {
    var link = ad.link || '#';
    var alt  = ad.alt  || 'Advertisement';
    var label = ad.label || '';
    var target = ad.newTab ? ' target="_blank" rel="noopener"' : '';
    return (
      '<li class="grid__item adrs-ad-card">' +
        '<a href="' + esc(link) + '" class="adrs-ad-link"' + target + ' aria-label="' + esc(alt) + '">' +
          '<img src="' + esc(ad.image) + '" alt="' + esc(alt) + '" class="adrs-ad-img" loading="lazy">' +
          (label ? '<div class="adrs-ad-label">' + esc(label) + '</div>' : '') +
        '</a>' +
      '</li>'
    );
  }

  function renderCardInner(p) {
    // ── Dawn 15.x native card — matches your Shopify default card exactly ──────
    // Shows: image → Sale badge (if on sale) → title → strikethrough + sale price

    // ── Image URL ─────────────────────────────────────────────────────────────
    var imgSrc = '';
    if (p.imageUrl && p.imageUrl.trim() !== '') {
      var rawUrl = p.imageUrl.trim();
      imgSrc = rawUrl.indexOf('?') !== -1
        ? rawUrl + '&width=533'
        : rawUrl + '?width=533';
    }

    // Media class: adapt uses media--adapt (no fixed height), others use fixed ratio
    var mediaClass = cfg.imageRatio === 'adapt'
      ? 'media media--transparent media--adapt'
      : 'media media--transparent';

    const imgHtml = imgSrc
      ? (
          '<div class="card__media">' +
            '<div class="' + mediaClass + '">' +
              '<img' +
                ' src="' + esc(imgSrc) + '"' +
                ' alt="' + esc(p.title) + '"' +
                ' loading="lazy"' +
                ' class="motion-reduce">' +
            '</div>' +
          '</div>'
        )
      : (
          '<div class="card__media">' +
            '<div class="' + mediaClass + '">' +
              '<div class="svg-wrapper">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" style="width:100%;height:100%;fill:#f3f3f3"><rect width="525.5" height="525.5"/></svg>' +
              '</div>' +
            '</div>' +
          '</div>'
        );

    // ── Sale / Sold out badge ─────────────────────────────────────────────────
    // compareAtPrice is only set when it is higher than the current price
    const onSale     = p.compareAtPrice && p.compareAtPrice > p.minPrice;
    const soldOut    = !p.inStock;

    // Use pre-calculated discount from DB (first variant price vs compare-at, matches Shopify)
    // Read both camelCase and PascalCase since API serialization may vary
    var discountPercent = p.maxDiscountPercent || p.MaxDiscountPercent || 0;

    const badge = soldOut
      ? '<div class="card__badge bottom left"><span class="badge badge--bottom-left color-scheme-2">Sold out</span></div>'
      : discountPercent > 0
        ? '<div class="collection__discount-badge" style="position:absolute;top:10px;left:10px;z-index:2;background:#e11d48;color:white;padding:0.25rem 0.6rem;font-size:20px;font-weight:600;border-radius:0;box-shadow:0 2px 4px rgba(0,0,0,0.1);">' +
            discountPercent + '% off' +
          '</div>'
        : '';

    // ── Price block — matches Dawn's price markup exactly ─────────────────────
    // On sale:   strikethrough original  +  bold sale price   (like your screenshot)
    // Regular:   single price
    // ── Price block ────────────────────────────────────────────────────────────
    // Matches Dawn's exact price markup including "From" prefix for price ranges.
    // Dawn shows "From $X" when a product has multiple variants at different prices.
    var isRange   = p.minPrice !== p.maxPrice;
    var fromPrefix = isRange ? 'From ' : '';

    var priceHtml;
    if (onSale) {
      // compare-at (strikethrough) LEFT + sale price RIGHT — matches your screenshot
      var salePrice    = fromPrefix + money(p.minPrice);
      var comparePrice = money(p.compareAtPrice);
      priceHtml =
        '<div class="price price--on-sale">' +
          '<div class="price__container">' +
            '<span class="visually-hidden">Regular price</span>' +
            '<s class="price-item price-item--regular">' +
              comparePrice +
            '</s>' +
            '<span class="visually-hidden">Sale price</span>' +
            '<span class="price-item price-item--sale price-item--last">' +
              salePrice +
            '</span>' +
          '</div>' +
        '</div>';
    } else {
      var regularPrice = isRange
        ? 'From ' + money(p.minPrice)
        : money(p.minPrice);
      priceHtml =
        '<div class="price' + (soldOut ? ' price--sold-out' : '') + '">' +
          '<div class="price__container">' +
            '<span class="visually-hidden">Price</span>' +
            '<span class="price-item price-item--regular">' +
              regularPrice +
            '</span>' +
          '</div>' +
        '</div>';
    }

    // ── Image ratio — match Dawn's section setting ──────────────────────────────
    // adapt   → no fixed ratio; image defines its own height (Dawn default)
    //           removes the .ratio class so the image renders at natural height
    // portrait → 125% padding-bottom (4:5 aspect ratio)
    // square   → 100% padding-bottom (1:1)
    var ratioClass, ratioStyle;
    switch (cfg.imageRatio) {
      case 'portrait':
        ratioClass = ' ratio';
        ratioStyle = '--ratio-percent: 125%;';
        break;
      case 'square':
        ratioClass = ' ratio';
        ratioStyle = '--ratio-percent: 100%;';
        break;
      case 'adapt':
      default:
        // No ratio class — image renders at its natural dimensions
        // This matches how Dawn renders cards when image_ratio = adapt
        ratioClass = '';
        ratioStyle = '';
        break;
    }

    return (
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
        '<div class="card card--standard' + (soldOut ? ' card--sold-out' : '') + ' card--media">' +

          '<div class="card__inner color-scheme-1 gradient' + ratioClass + '" style="' + ratioStyle + '">' +
            imgHtml +
            '<div class="card__overlay"></div>' +
            badge +
          '</div>' +

          '<div class="card__content">' +
            '<div class="card__information">' +
              '<h3 class="card__heading h5">' +
                '<a href="/products/' + esc(p.handle) + '"' +
                   ' class="full-unstyled-link"' +
                   ' aria-label="' + esc(p.title) + '">' +
                  esc(p.title) +
                '</a>' +
              '</h3>' +
              '<div class="card-information">' +
                priceHtml +
              '</div>' +
            '</div>' +
            (cfg.quickAdd !== 'none' ?
              '<div class="card__footer">' +
                '<div class="card__button quick-add no-js-hidden">' +
                  '<button class="button button--full-width adrs-quickview-btn color-' + cfg.cardColorScheme + '"' +
                    ' data-handle="' + esc(p.handle) + '"' +
                    (soldOut ? ' disabled' : '') + '>' +
                    (soldOut ? 'Sold Out' : 'Choose Options') +
                  '</button>' +
                '</div>' +
              '</div>'
            : '') +
          '</div>' +

        '</div>' +
      '</div>'
    );
  }

  // ── Pagination ──────────────────────────────────────────────────────────────

  function renderPagination(page, limit, total) {
    var el = els.paginationEl;
    if (!el) return;

    var pages = Math.ceil(total / limit);
    if (pages <= 1) { el.innerHTML = ''; return; }

    var delta = 2;
    var left  = Math.max(1, page - delta);
    var right = Math.min(pages, page + delta);
    var html  = '<nav class="pagination" role="navigation" aria-label="Pagination"><div class="pagination__list list-unstyled">';

    if (left > 1) {
      html += pageBtn(1, page);
      if (left > 2) html += '<li><span class="pagination__item">&#8230;</span></li>';
    }
    for (var i = left; i <= right; i++) html += pageBtn(i, page);
    if (right < pages) {
      if (right < pages - 1) html += '<li><span class="pagination__item">&#8230;</span></li>';
      html += pageBtn(pages, page);
    }

    html += '</div></nav>';
    el.innerHTML = html;

    el.querySelectorAll('.adrs-page-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.page = parseInt(btn.dataset.page, 10);
        loadProducts();
      });
    });
  }

  function pageBtn(i, current) {
    if (i === current) {
      return '<li><span class="pagination__item pagination__item--current" aria-current="page">' + i + '</span></li>';
    }
    return '<li><button class="pagination__item link adrs-page-btn" data-page="' + i + '" aria-label="Page ' + i + '">' + i + '</button></li>';
  }


    // ── Facet sidebar ─────────────────────────────────────────────────────────

  function renderFacets(facets, priceRange) {
    if (!els.facetsInner) return;

    let html = '';

    // ── Price range slider — top ──────────────────────────────────────────────
    // NOTE: prices from the API are in DOLLARS (e.g. 101.20), not cents
    if (priceRange && priceRange.max > 0) {
      const minDollars = Math.floor(priceRange.min);
      const maxDollars = Math.ceil(priceRange.max);
      const curMin = (state.filters['price'] && state.filters['price'].min) || minDollars;
      const curMax = (state.filters['price'] && state.filters['price'].max) || maxDollars;
      html += (
        '<div class="adrsearch-facet adrsearch-facet--price" data-price-min="' + minDollars + '" data-price-max="' + maxDollars + '">' +
          '<button class="adrsearch-facet__toggle" aria-expanded="true">' +
            '<span class="adrsearch-facet__title">PRICE</span>' +
            '<span class="adrsearch-facet__chevron">−</span>' +
          '</button>' +
          '<div class="adrsearch-slider-wrap">' +
            '<div class="adrsearch-slider-labels">' +
              '<span id="adrsearch-price-label-min">$' + Math.floor(curMin) + '</span>' +
              '<span id="adrsearch-price-label-max">$' + Math.ceil(curMax) + '</span>' +
            '</div>' +
            '<div class="adrsearch-slider-track" style="height:4px;position:relative;">' +
              '<div class="adrsearch-slider-range" id="adrsearch-slider-range"></div>' +
              '<input type="range" class="adrsearch-slider adrsearch-slider--min" id="adrsearch-price-min"' +
                ' min="' + minDollars + '" max="' + maxDollars + '" value="' + Math.floor(curMin) + '" step="1">' +
              '<input type="range" class="adrsearch-slider adrsearch-slider--max" id="adrsearch-price-max"' +
                ' min="' + minDollars + '" max="' + maxDollars + '" value="' + Math.ceil(curMax) + '" step="1">' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    // Render facets — pill style for size/runner, minimal checkbox for others
    var facetIndex = 0;
    facets.forEach(function (f) {
      if (!f.values || f.values.length === 0) return;
      const facetKey  = f.namespace + '.' + f.key;
      const isPill    = false; // minimal line style (isPill=false uses line renderer)
      const isOpen    = facetIndex === 0;
      facetIndex++;

      html += '<div class="adrsearch-facet adrsearch-facet--' + (isPill ? 'pill' : 'check') + '" data-facet-key="' + esc(facetKey) + '">';
      html += '<button class="adrsearch-facet__toggle" aria-expanded="' + isOpen + '">';
      html += '<span class="adrsearch-facet__title">' + esc(f.label) + '</span>';
      html += '<span class="adrsearch-facet__chevron">' + (isOpen ? '−' : '+') + '</span>';
      html += '</button>';

      const activeVals = state.filters[facetKey] || [];

      if (isPill) {
        // ── Pill style ──────────────────────────────────────────────────────
        html += '<div class="adrsearch-facet__pills"' + (isOpen ? '' : ' style="display:none"') + '>';
        var maxVals = (f.key === 'std_size' || f.key === 'runner_size') ? 50 : 30;
      f.values.slice(0, maxVals).forEach(function (v) {
          const isActive = activeVals.includes(v.value);
          html += '<button class="adrsearch-pill-btn' + (isActive ? ' is-active' : '') + '"' +
                  ' data-facet-key="' + esc(facetKey) + '"' +
                  ' data-facet-value="' + esc(v.value) + '">' +
                  esc(v.value) +
                  '<span class="adrsearch-pill-btn__count">(' + v.count.toLocaleString() + ')</span>' +
                '</button>';
        });
        html += '</div>';
      } else {
        // ── Minimal checkbox/line style ─────────────────────────────────────
        html += '<ul class="adrsearch-facet__values"' + (isOpen ? '' : ' style="display:none"') + '>';
        f.values.slice(0, 25).forEach(function (v) {
          const cbId     = 'cb-' + facetKey.replace(/[.\s]/g, '-') + '-' + v.value.replace(/[\s\/]/g, '-');
          const isActive = activeVals.includes(v.value);
          html += (
            '<li>' +
              '<label class="adrsearch-facet__label' + (isActive ? ' is-active' : '') + '" for="' + esc(cbId) + '">' +
                '<input type="checkbox" class="adrsearch-facet-cb"' +
                  ' id="' + esc(cbId) + '"' +
                  ' data-facet-key="' + esc(facetKey) + '"' +
                  ' data-facet-value="' + esc(v.value) + '"' +
                  (isActive ? ' checked' : '') + '>' +
                '<span class="adrsearch-facet__value-text">' + esc(v.value) + '</span>' +
                '<span class="adrsearch-facet__count">' + v.count.toLocaleString() + '</span>' +
              '</label>' +
            '</li>'
          );
        });
        html += '</ul>';
      }

      html += '</div>';
    });

    // ── Availability (In Stock / Out of Stock) — after metafield facets ─────────
    const stockFilter    = state.filters['availability'] || [];
    const inStockActive  = stockFilter.includes('in_stock');
    const outStockActive = stockFilter.includes('out_of_stock');
    html += (
      '<div class="adrsearch-facet adrsearch-facet--check" data-facet-key="availability">' +
        '<button class="adrsearch-facet__toggle" aria-expanded="true">' +
          '<span class="adrsearch-facet__title">AVAILABILITY</span>' +
          '<span class="adrsearch-facet__chevron">−</span>' +
        '</button>' +
        '<ul class="adrsearch-facet__values">' +
          '<li>' +
            '<label class="adrsearch-facet__label' + (inStockActive ? ' is-active' : '') + '" for="cb-availability-in_stock">' +
              '<input type="checkbox" class="adrsearch-facet-cb" id="cb-availability-in_stock"' +
                ' data-facet-key="availability" data-facet-value="in_stock"' +
                (inStockActive ? ' checked' : '') + '>' +
              '<span class="adrsearch-facet__value-text">In Stock</span>' +
            '</label>' +
          '</li>' +
          '<li>' +
            '<label class="adrsearch-facet__label' + (outStockActive ? ' is-active' : '') + '" for="cb-availability-out_of_stock">' +
              '<input type="checkbox" class="adrsearch-facet-cb" id="cb-availability-out_of_stock"' +
                ' data-facet-key="availability" data-facet-value="out_of_stock"' +
                (outStockActive ? ' checked' : '') + '>' +
              '<span class="adrsearch-facet__value-text">Out of Stock</span>' +
            '</label>' +
          '</li>' +
        '</ul>' +
      '</div>'
    );

    els.facetsInner.innerHTML = html;

    // ── Wire pill buttons ──────────────────────────────────────────────────
    els.facetsInner.querySelectorAll('.adrsearch-pill-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const key = btn.dataset.facetKey;
        const val = btn.dataset.facetValue;

        if (!state.filters[key]) state.filters[key] = [];

        if (btn.classList.contains('is-active')) {
          // Deselect
          btn.classList.remove('is-active');
          state.filters[key] = state.filters[key].filter(function (v) { return v !== val; });
          if (state.filters[key].length === 0) delete state.filters[key];
        } else {
          // Select
          btn.classList.add('is-active');
          if (!state.filters[key].includes(val)) state.filters[key].push(val);
        }

        updateActivePills();
        activateApiMode();
      });
    });

    // ── Wire checkboxes ────────────────────────────────────────────────────
    els.facetsInner.querySelectorAll('.adrsearch-facet-cb').forEach(function (cb) {
      cb.addEventListener('change', function () {
        if (cb._silent) return;

        const key = cb.dataset.facetKey;
        const val = cb.dataset.facetValue;

        if (!state.filters[key]) state.filters[key] = [];

        if (cb.checked) {
          cb.closest('.adrsearch-facet__label').classList.add('is-active');
          if (!state.filters[key].includes(val)) state.filters[key].push(val);
        } else {
          cb.closest('.adrsearch-facet__label').classList.remove('is-active');
          state.filters[key] = state.filters[key].filter(function (v) { return v !== val; });
          if (state.filters[key].length === 0) delete state.filters[key];
        }

        updateActivePills();
        activateApiMode();
      });
    });

    // ── Collapse/expand toggle ─────────────────────────────────────────────
    els.facetsInner.querySelectorAll('.adrsearch-facet__toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const facet    = btn.closest('.adrsearch-facet');
        const list     = facet.querySelector('.adrsearch-facet__pills, .adrsearch-facet__values');
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !expanded);
        if (list) list.style.display = expanded ? 'none' : '';
        btn.querySelector('.adrsearch-facet__chevron').textContent = expanded ? '+' : '−';
      });
    });

    // Price slider wiring
    (function () {
      const minSlider = document.getElementById('adrsearch-price-min');
      const maxSlider = document.getElementById('adrsearch-price-max');
      const labelMin  = document.getElementById('adrsearch-price-label-min');
      const labelMax  = document.getElementById('adrsearch-price-label-max');
      const rangeEl   = document.getElementById('adrsearch-slider-range');
      if (!minSlider || !maxSlider) return;

      var priceDebounce = null;

      function updateSliderUI() {
        const min    = parseInt(minSlider.min);
        const max    = parseInt(minSlider.max);
        const curMin = parseInt(minSlider.value);
        const curMax = parseInt(maxSlider.value);
        const pct1   = ((curMin - min) / (max - min)) * 100;
        const pct2   = ((curMax - min) / (max - min)) * 100;
        if (labelMin) labelMin.textContent = '$' + curMin;
        if (labelMax) labelMax.textContent = '$' + curMax;
        if (rangeEl)  rangeEl.style.left  = pct1 + '%';
        if (rangeEl)  rangeEl.style.width = (pct2 - pct1) + '%';
      }

      function applyPriceFilter() {
        const absMin = parseInt(minSlider.min);
        const absMax = parseInt(minSlider.max);
        const curMin = parseInt(minSlider.value);
        const curMax = parseInt(maxSlider.value);
        // Only set filter if user has moved handles away from the absolute bounds
        if (curMin === absMin && curMax === absMax) {
          delete state.filters['price'];
        } else {
          state.filters['price'] = { min: curMin, max: curMax };
        }
        updateActivePills();
        activateApiMode();
      }

      minSlider.addEventListener('input', function () {
        // Prevent min from overtaking max
        if (parseInt(minSlider.value) > parseInt(maxSlider.value)) {
          minSlider.value = maxSlider.value;
        }
        updateSliderUI();
        clearTimeout(priceDebounce);
        priceDebounce = setTimeout(applyPriceFilter, 400);
      });

      maxSlider.addEventListener('input', function () {
        // Prevent max from going below min
        if (parseInt(maxSlider.value) < parseInt(minSlider.value)) {
          maxSlider.value = minSlider.value;
        }
        updateSliderUI();
        clearTimeout(priceDebounce);
        priceDebounce = setTimeout(applyPriceFilter, 400);
      });

      // Initial UI sync
      updateSliderUI();
    })();
  }

  // ── Active filter pills ───────────────────────────────────────────────────

  function updateActivePills() {
    if (!els.activeFilters) return;

    const pills = [];
    Object.keys(state.filters).forEach(function (key) {
      const val = state.filters[key];

      if (key === 'availability') return;  // no pill for in_stock / out_of_stock

      if (key === 'price' && typeof val === 'object') {
        const label = (val.min ? '$' + Math.floor(val.min) : '') +
                      (val.min && val.max ? ' – ' : '') +
                      (val.max ? '$' + Math.ceil(val.max) : '');
        pills.push(makePill('Price: ' + label, key, null));
        return;
      }

      if (Array.isArray(val)) {
        val.forEach(function (v) {
          pills.push(makePill(v, key, v));
        });
      }
    });

    if (pills.length === 0) {
      els.activeFilters.innerHTML = '';
      return;
    }

    els.activeFilters.innerHTML =
      pills.join('') +
      '<button class="adrsearch-pill adrsearch-pill--clear" id="adrsearch-pill-clear-all">' +
        'Clear all' +
      '</button>';

    // Wire individual pill removals
    els.activeFilters.querySelectorAll('.adrsearch-pill[data-key]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        const key = pill.dataset.key;
        const val = pill.dataset.val;

        if (!val) {
          // Price pill: remove entire price key
          delete state.filters[key];
        } else {
          state.filters[key] = (state.filters[key] || []).filter(function (v) { return v !== val; });
          if (state.filters[key].length === 0) delete state.filters[key];
        }

        // Uncheck the matching checkbox in sidebar
        if (els.facetsInner) {
          els.facetsInner.querySelectorAll('.adrsearch-facet-cb').forEach(function (cb) {
            if (cb.dataset.facetKey === key && cb.dataset.facetValue === val) cb.checked = false;
          });
        }

        updateActivePills();

        // If all filters removed, use clearAllFilters to handle properly
        if (Object.keys(state.filters).length === 0 && !state.sort && !state.query) {
          clearAllFilters();
        } else {
          activateApiMode();
        }
      });
    });

    // Clear all
    const clearAllBtn = document.getElementById('adrsearch-pill-clear-all');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', clearAllFilters);
    }
  }

  function makePill(label, key, val) {
    const valAttr = val ? ' data-val="' + esc(val) + '"' : '';
    return (
      '<button class="adrsearch-pill" data-key="' + esc(key) + '"' + valAttr + '>' +
        esc(label) + ' ✕' +
      '</button>'
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UI HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  function showLoading(on) {
    // Dawn: show loading overlay on the grid container div, not the <ul> itself
    if (els.loading) {
      els.loading.style.display = on ? 'flex' : 'none';
    }
  }

  function showGrid(on) {
    // Dawn's product grid is a <ul> — toggle visibility so layout space is preserved
    // while loading, preventing page height jumps
    if (els.grid) {
      els.grid.style.visibility = on ? 'visible' : 'hidden';
      els.grid.style.opacity    = on ? '1' : '0';
    }
  }

  function showEmpty(on) {
    if (els.empty) els.empty.style.display = on ? 'flex' : 'none';
  }

  function updateCount(n) {
    if (els.count) {
      var label = n.toLocaleString() + ' product' + (n === 1 ? '' : 's');
      if (state.query) label += ' for "' + state.query + '"';
      els.count.textContent = label;
    }
  }

  function clearAllFilters() {
    state.filters = { 'availability': ['in_stock'] };  // reset to default
    state.sort    = '';
    state.query   = '';
    state.page    = 1;
    updateActivePills();

    // Uncheck all checkboxes, then re-check In Stock
    if (els.facetsInner) {
      els.facetsInner.querySelectorAll('.adrsearch-facet-cb').forEach(function (cb) {
        var isDefault = cb.dataset.facetKey === 'availability' && cb.dataset.facetValue === 'in_stock';
        cb._silent = true;
        cb.checked = isDefault;
        var lbl = cb.closest('.adrsearch-facet__label');
        if (lbl) {
          if (isDefault) lbl.classList.add('is-active');
          else lbl.classList.remove('is-active');
        }
        cb._silent = false;
      });
    }

    // Reset price inputs
    // Reset price sliders to absolute bounds
    var minSlider = document.getElementById('adrsearch-price-min');
    var maxSlider = document.getElementById('adrsearch-price-max');
    if (minSlider) minSlider.value = minSlider.min;
    if (maxSlider) maxSlider.value = maxSlider.max;
    var labelMin = document.getElementById('adrsearch-price-label-min');
    var labelMax = document.getElementById('adrsearch-price-label-max');
    var rangeEl  = document.getElementById('adrsearch-slider-range');
    if (labelMin && minSlider) labelMin.textContent = '$' + minSlider.min;
    if (labelMax && maxSlider) labelMax.textContent = '$' + maxSlider.max;
    if (rangeEl) { rangeEl.style.left = '0%'; rangeEl.style.width = '100%'; }

    // Reset sort select
    if (els.sort) els.sort.value = '';

    // Update URL — remove all filter/sort params
    pushUrl();

    if (state.apiMode) {
      // Clear grid and reload from page 1 with no filters
      if (els.grid) els.grid.innerHTML = '';
      showEmpty(false);
      showGrid(true);
      loadProducts();
    } else {
      // Not in API mode — reload the page to get Shopify-rendered content back
      window.location.href = window.location.pathname;
    }
  }

  // ── Mobile sidebar ────────────────────────────────────────────────────────

  function openSidebar() {
    if (els.sidebar)  els.sidebar.classList.add('is-open');
    if (els.overlay)  els.overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  }

  function closeSidebar() {
    if (els.sidebar)  els.sidebar.classList.remove('is-open');
    if (els.overlay)  els.overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ENCODERS / FORMATTERS
  // ═══════════════════════════════════════════════════════════════════════════

  function encodeFilters(filters) {
    if (!Object.keys(filters).length) return '';
    try { return btoa(unescape(encodeURIComponent(JSON.stringify(filters)))); }
    catch (e) { return ''; }
  }

  function money(amount) {
    // Prices from the API are stored and returned as DOLLAR amounts (e.g. 101.20)
    // NOT as cents. Do not divide by 100.
    if (amount === null || amount === undefined) return '';
    const dollars = parseFloat(amount);
    if (isNaN(dollars)) return '';

    // Use Shopify's money_format from Liquid if available
    const fmt = window.AdrSearch && window.AdrSearch.moneyFormat;
    if (fmt) {
      const fixed = dollars.toFixed(2);
      return fmt
        .replace('{{amount}}', fixed)
        .replace('{{amount_no_decimals}}', String(Math.floor(dollars)))
        .replace('{{amount_with_comma_separator}}', fixed.replace('.', ','));
    }
    // Fallback: plain dollar format
    const n = dollars.toFixed(2);
    return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EVENT WIRING + INIT
  // Wrapped in DOMContentLoaded so this is safe even if the <script> tag
  // appears before the sidebar/toolbar HTML in the DOM. Combined with the
  // getter-based els object above, element lookup always happens after the
  // full page has parsed.
  // ═══════════════════════════════════════════════════════════════════════════


  // ── Search ──────────────────────────────────────────────────────────────────

  function activateSearch(q) {
    state.query   = q.trim();
    // Reload facets in global mode for search results
    loadFacets();
    state.page    = 1;
    state.filters = { 'availability': ['in_stock'] };  // reset to default when searching
    if (els.grid) els.grid.innerHTML = '';
    if (els.facetsInner) {
      // uncheck all checkboxes
      els.facetsInner.querySelectorAll('.adrsearch-facet-cb').forEach(function (cb) {
        cb.checked = false;
      });
    }
    updateActivePills();
    pushUrl();
    if (!state.apiMode) {
      state.apiMode = true;
      if (els.shopifyPagination) els.shopifyPagination.style.display = 'none';
    }
    loadProducts();
  }

  function clearSearch() {
    state.query = '';
    state.page  = 1;
    if (els.searchInput)  els.searchInput.value = '';
    if (els.searchClear)  els.searchClear.style.display = 'none';
    if (els.grid)         els.grid.innerHTML = '';
    pushUrl();
    // Reload facets so counts go back to full collection (no q scoping)
    loadFacets();
    // Reload collection normally
    if (state.apiMode) loadProducts();
    else {
      // Return to Shopify-rendered page 1
      window.location.href = window.location.pathname;
    }
  }

  function initSearch() {
    var input = els.searchInput;
    var clear = els.searchClear;
    if (!input) return;

    var _searchTimer = null;

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (clear) clear.style.display = q ? '' : 'none';

      clearTimeout(_searchTimer);
      if (q.length === 0) {
        clearSearch();
        return;
      }
      if (q.length < 2) return;

      _searchTimer = setTimeout(function () {
        activateSearch(q);
      }, 400);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        clearTimeout(_searchTimer);
        var q = input.value.trim();
        if (q.length >= 2) activateSearch(q);
      }
    });

    if (clear) {
      clear.addEventListener('click', function () {
        clearSearch();
      });
    }

    // ── Intercept Shopify predictive search dropdown clicks ──────────────
    // Dawn's predictive-search component navigates to /search?q=... when
    // a suggestion is clicked. Intercept and redirect to /collections/all?q=
    // so AdrSearch handles the results instead of Shopify's native search.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/search?q="], a[href*="/search?type="]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      var match = href.match(/[?&]q=([^&]*)/);
      if (!match) return;

      var q = decodeURIComponent(match[1].replace(/\+/g, ' ')).trim();
      if (!q) return;

      e.preventDefault();
      e.stopPropagation();

      // Redirect to collections/all with our search param
      window.location.href = '/collections/all?q=' + encodeURIComponent(q);
    }, true); // capture phase — fires before Dawn's own handlers

    // Also intercept form submits from Shopify's header search form
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.action === undefined) return;

      var action = form.getAttribute('action') || '';
      if (!action.includes('/search')) return;

      var qInput = form.querySelector('input[name="q"]');
      if (!qInput) return;

      var q = (qInput.value || '').trim();
      if (!q) return;

      e.preventDefault();
      window.location.href = '/collections/all?q=' + encodeURIComponent(q);
    }, true);
  }

  function init() {

    // Sort — HANDOFF POINT
    if (els.sort) {
      els.sort.addEventListener('change', function () {
        state.sort = els.sort.value;
        activateApiMode(); // ← sort triggers API
      });
    }

    // Clear all (empty state button)
    if (els.clearBtn) {
      els.clearBtn.addEventListener('click', clearAllFilters);
    }

    // Mobile sidebar toggle
    if (els.mobileFilterBtn) {
      els.mobileFilterBtn.addEventListener('click', openSidebar);
    }
    if (els.sidebarClose) {
      els.sidebarClose.addEventListener('click', closeSidebar);
    }
    if (els.overlay) {
      els.overlay.addEventListener('click', closeSidebar);
    }

    // External filter events (from your existing facet widget, if any)
    document.addEventListener('adrsearch:filters-changed', function (e) {
      state.filters = (e.detail && e.detail.filters) || {};
      updateActivePills();
      activateApiMode();
    });

    // Browser back / forward — restore state from URL
    window.addEventListener('popstate', function () {
      var restored = readUrl();
      state.filters = restored;
      state.sort    = state.sort; // already set by readUrl()
      updateActivePills();

      // Re-check sidebar checkboxes to match restored filters
      if (els.facetsInner) {
        els.facetsInner.querySelectorAll('.adrsearch-facet-cb').forEach(function (cb) {
          var key  = cb.dataset.facetKey;
          var val  = cb.dataset.facetValue;
          var vals = state.filters[key];
          cb.checked = Array.isArray(vals) && vals.includes(val);
        });
        // Restore price inputs
        var priceFilter = state.filters['price'] || {};
        var minEl = document.getElementById('adrsearch-price-min');
        var maxEl = document.getElementById('adrsearch-price-max');
        if (minEl && priceFilter.min) minEl.value = priceFilter.min;
        if (maxEl && priceFilter.max) maxEl.value = priceFilter.max;
        // Sync slider UI labels and range highlight
        var labelMin = document.getElementById('adrsearch-price-label-min');
        var labelMax = document.getElementById('adrsearch-price-label-max');
        var rangeEl  = document.getElementById('adrsearch-slider-range');
        if (minEl && maxEl && rangeEl) {
          var mn = parseInt(minEl.min), mx = parseInt(minEl.max);
          var p1 = ((parseInt(minEl.value) - mn) / (mx - mn)) * 100;
          var p2 = ((parseInt(maxEl.value) - mn) / (mx - mn)) * 100;
          if (labelMin) labelMin.textContent = '$' + minEl.value;
          if (labelMax) labelMax.textContent = '$' + maxEl.value;
          rangeEl.style.left  = p1 + '%';
          rangeEl.style.width = (p2 - p1) + '%';
        }
      }

      var hasFilters = Object.keys(state.filters).length > 0 || state.sort;
      if (hasFilters) {
        activateApiMode();
      } else {
        // Back to unfiltered — reload the page to show Shopify-rendered products
        window.location.reload();
      }
    });

    // ── Init ────────────────────────────────────────────────────────────────
    // If product count is ≤5000, Shopify native filters handle everything.
    // AdrSearch only activates for large collections (>5000 products).
    if (!cfg.useAdrSearch) {
      console.log('[AdrSearch] Product count ≤5000 — using Shopify native filters.');
      return;
    }

    // Mark body so CSS hides Dawn's native filter UI for this collection
    document.body.setAttribute('data-adrsearch', 'active');

    // Detect sticky header height and set CSS variable so sidebar top/height are correct
    (function setHeaderHeight() {
      var header = document.querySelector('header, .header-wrapper, sticky-header, #shopify-section-header');
      if (header) {
        var h = header.getBoundingClientRect().height;
        if (h > 0) document.body.style.setProperty('--adrs-header-height', h + 'px');
      }
    })();

    // Always show AdrSearch sidebar — on collection pages AND search results
    // Read URL state FIRST so state.query is set before loadFacets runs
    var urlFilters = readUrl();

    if (cfg.handle || state.query) {
      // Hide Shopify's native filter panel — we replace it with our sidebar
      var shopifyFilters = document.querySelector('.facets__form, #FacetFiltersForm, .facets-container, [data-section-type="collection-filters"]');
      if (shopifyFilters) shopifyFilters.style.display = 'none';

      // If URL has any filters, use them (overrides default in_stock).
      // If URL has availability explicitly set (even to nothing), respect it.
      // If URL has NO filters at all, keep the default { availability: ['in_stock'] }.
      var hasUrlFilters = Object.keys(urlFilters).length > 0 || state.sort;
      if (hasUrlFilters) {
        // URL is source of truth — if user cleared availability, urlFilters won't have it
        state.filters = urlFilters;
      }
      // else: keep state.filters default { availability: ['in_stock'] }

      // Load facets — uses state.query to decide global vs collection-scoped
      loadFacets();

      // ── Restore filters from URL (back navigation / shared links) ───────────
      if (hasUrlFilters) {
        state.filters = urlFilters;
        setTimeout(function () {
          updateActivePills();
          if (els.facetsInner) {
            els.facetsInner.querySelectorAll('.adrsearch-facet-cb').forEach(function (cb) {
              var key  = cb.dataset.facetKey;
              var val  = cb.dataset.facetValue;
              var vals = state.filters[key];
              // Use _silent flag to prevent the change handler from firing
              cb._silent = true;
              cb.checked = Array.isArray(vals) && vals.includes(val);
              // Also update label active class for line-style facets
              var lbl = cb.closest('.adrsearch-facet__label');
              if (lbl) {
                if (cb.checked) lbl.classList.add('is-active');
                else lbl.classList.remove('is-active');
              }
              cb._silent = false;
            });
          }
        }, 500);
      }

      // If URL has filters, reload from page 1 via API
      if (hasUrlFilters) {
        if (els.grid) els.grid.innerHTML = '';
        pushUrl();
        loadProducts();
      }

      initSearch();

      // If URL has a query param, activate search immediately
      if (state.query) {
        activateSearch(state.query);
      }

      console.log('[AdrSearch] AdrSearch ready. Total:', state.total);

      // ── Inject ads into Shopify-native first-load grid ───────────────────
      // On first load products are rendered by Liquid — inject ad cards into
      // the existing grid items so ads appear before AdrSearch takes over.
      injectAdsIntoNativeGrid();

    } else {
      console.warn('[AdrSearch] No collection handle found on this page.');
    }
  }

  function injectAdsIntoNativeGrid() {
    var ads = cfg.ads;
    if (!ads || ads.length === 0) return;
    var grid = els.grid;
    if (!grid) return;

    // Get all existing native product items
    var items = grid.querySelectorAll('.grid__item:not(.adrs-ad-card)');
    if (items.length === 0) return;

    // Build list of positions where we need to insert ads
    // Process in reverse order so insertions don't shift indices
    var insertions = []; // { afterIndex, ad }
    for (var ai = 0; ai < ads.length; ai++) {
      var ad = ads[ai];
      if (!ad || !ad.position || !ad.image) continue;
      if (ad.repeat) {
        for (var pos = ad.position; pos <= items.length; pos += ad.position) {
          insertions.push({ afterIndex: pos - 1, ad: ad });
        }
      } else if (ad.position <= items.length) {
        insertions.push({ afterIndex: ad.position - 1, ad: ad });
      }
    }

    // Sort by afterIndex descending so we insert from bottom to top
    insertions.sort(function(a, b) { return b.afterIndex - a.afterIndex; });

    insertions.forEach(function(ins) {
      var adEl = document.createElement('li');
      adEl.className = 'grid__item adrs-ad-card';
      var link = ins.ad.link || '#';
      var alt  = ins.ad.alt  || 'Advertisement';
      var label = ins.ad.label || '';
      var target = ins.ad.newTab ? ' target="_blank" rel="noopener"' : '';
      adEl.innerHTML =
        '<a href="' + esc(link) + '" class="adrs-ad-link"' + target + ' aria-label="' + esc(alt) + '">' +
          '<img src="' + esc(ins.ad.image) + '" alt="' + esc(alt) + '" class="adrs-ad-img" loading="lazy">' +
          (label ? '<div class="adrs-ad-label">' + esc(label) + '</div>' : '') +
        '</a>';

      // Insert after the item at afterIndex
      var refItem = items[ins.afterIndex];
      if (refItem && refItem.nextSibling) {
        grid.insertBefore(adEl, refItem.nextSibling);
      } else if (refItem) {
        grid.appendChild(adEl);
      }
    });
  }

  // Run after DOM is fully parsed regardless of where the <script> tag is
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already ready (script is at bottom of body or deferred)
    init();
  }



  // ── Quick View Modal ──────────────────────────────────────────────────────

  // Inject modal HTML into body once
  function initQuickView() {
    if (document.getElementById('adrs-qv-modal')) return;
    var modal = document.createElement('div');
    modal.id = 'adrs-qv-modal';
    modal.innerHTML = [
      '<div id="adrs-qv-overlay"></div>',
      '<div id="adrs-qv-dialog" role="dialog" aria-modal="true">',
        '<button id="adrs-qv-close" aria-label="Close">&times;</button>',
        '<div id="adrs-qv-content"><div class="adrs-qv-loading">Loading...</div></div>',
      '</div>'
    ].join('');
    document.body.appendChild(modal);

    document.getElementById('adrs-qv-overlay').addEventListener('click', closeQuickView);
    document.getElementById('adrs-qv-close').addEventListener('click', closeQuickView);
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeQuickView();
    });
  }

  function openQuickView(handle) {
    initQuickView();
    document.getElementById('adrs-qv-modal').style.display = 'block';
    document.body.style.overflow = 'hidden';
    document.getElementById('adrs-qv-content').innerHTML = '<div class="adrs-qv-loading">Loading...</div>';

    fetch('/products/' + handle + '.js')
      .then(function(r) { return r.json(); })
      .then(function(product) { renderQuickView(product); })
      .catch(function() {
        document.getElementById('adrs-qv-content').innerHTML = '<p style="padding:2rem">Failed to load product.</p>';
      });
  }

  function closeQuickView() {
    var modal = document.getElementById('adrs-qv-modal');
    if (modal) modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  function renderQuickView(product) {
    var variant = product.variants[0];
    // Ensure image URL has https: prefix (Shopify sometimes returns protocol-relative URLs)
    var rawImg = (product.images && product.images[0]) ? product.images[0].src : '';
    var img = rawImg ? (rawImg.startsWith('//') ? 'https:' + rawImg : rawImg) : '';
    // Use a larger image size for the quick view modal
    img = img ? img.replace(/(\.[^.?]+)(\?.*)?$/, '_800x$1$2') : '';

    // Build breadcrumb
    var breadcrumb = '<a href="/">Home</a> &rsaquo; <span>' + esc(product.title) + '</span>';

    // Discount badge
    var discHtml = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      var disc = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
      discHtml = '<div class="adrs-qv-badge">' + disc + '% off</div>';
    }

    // Option selectors
    var optionsHtml = '';
    product.options.forEach(function(opt, oi) {
      var values = opt.values;
      optionsHtml += '<div class="adrs-qv-option">';
      optionsHtml += '<label class="adrs-qv-opt-label">' + esc(opt.name) + '</label>';
      optionsHtml += '<div class="adrs-qv-opt-btns">';
      values.forEach(function(val) {
        var active = variant['option' + (oi+1)] === val ? ' adrs-qv-opt-active' : '';
        optionsHtml += '<button class="adrs-qv-opt-btn' + active + '" data-opt-index="' + oi + '" data-opt-value="' + esc(val) + '">' + esc(val) + '</button>';
      });
      optionsHtml += '</div></div>';
    });

    // Price
    var priceHtml = formatMoney(variant.price);
    var comparePriceHtml = (variant.compare_at_price && variant.compare_at_price > variant.price)
      ? '<s style="color:#999;margin-left:8px">' + formatMoney(variant.compare_at_price) + '</s>'
      : '';

    // Metafield info badges (from DOM data if available)
    var infoHtml = '';

    document.getElementById('adrs-qv-content').innerHTML = [
      '<div class="adrs-qv-inner">',
        '<div class="adrs-qv-breadcrumb">' + breadcrumb + '</div>',
        '<div class="adrs-qv-body">',
          '<div class="adrs-qv-img-wrap">',
            '<img src="' + esc(img) + '" alt="' + esc(product.title) + '" class="adrs-qv-img">',
          '</div>',
          '<div class="adrs-qv-info">',
            '<p class="adrs-qv-vendor">' + esc(product.vendor) + '</p>',
            discHtml,
            '<h2 class="adrs-qv-title">' + esc(product.title) + '</h2>',
            '<div class="adrs-qv-price">' + priceHtml + comparePriceHtml + '</div>',
            optionsHtml,
            '<div class="adrs-qv-qty-row">',
              '<label class="adrs-qv-opt-label">Quantity</label>',
              '<div class="adrs-qv-qty">',
                '<button class="adrs-qv-qty-btn" id="adrs-qv-minus">−</button>',
                '<input type="number" id="adrs-qv-qty" value="1" min="1" max="99">',
                '<button class="adrs-qv-qty-btn" id="adrs-qv-plus">+</button>',
              '</div>',
            '</div>',
            '<button class="adrs-qv-atc button button--full-width" id="adrs-qv-atc" data-variant-id="' + variant.id + '">',
              variant.available ? 'Add to Cart' : 'Sold Out',
            '</button>',
            '<a href="/products/' + esc(product.handle) + '" class="adrs-qv-fulllink">View full details &rarr;</a>',
          '</div>',
        '</div>',
      '</div>'
    ].join('');

    // Wire up option buttons
    var selectedOptions = product.variants[0].options.slice();
    function updateSelectedVariant() {
      var matched = product.variants.find(function(v) {
        return v.options.every(function(o, i) { return o === selectedOptions[i]; });
      }) || product.variants[0];

      // Update price
      var p2 = formatMoney(matched.price);
      var cp2 = (matched.compare_at_price && matched.compare_at_price > matched.price)
        ? '<s style="color:#999;margin-left:8px">' + formatMoney(matched.compare_at_price) + '</s>' : '';
      document.querySelector('.adrs-qv-price').innerHTML = p2 + cp2;

      // Update discount badge
      var db = document.querySelector('.adrs-qv-badge');
      if (matched.compare_at_price && matched.compare_at_price > matched.price) {
        var d2 = Math.round((matched.compare_at_price - matched.price) * 100 / matched.compare_at_price);
        if (db) db.textContent = d2 + '% off';
        else {
          var dbNew = document.createElement('div');
          dbNew.className = 'adrs-qv-badge';
          dbNew.textContent = d2 + '% off';
          document.querySelector('.adrs-qv-title').before(dbNew);
        }
      } else if (db) db.remove();

      // Update ATC button
      var atcBtn = document.getElementById('adrs-qv-atc');
      atcBtn.dataset.variantId = matched.id;
      atcBtn.textContent = matched.available ? 'Add to Cart' : 'Sold Out';
      atcBtn.disabled = !matched.available;
    }

    document.getElementById('adrs-qv-content').addEventListener('click', function(e) {
      // Option button click
      if (e.target.classList.contains('adrs-qv-opt-btn')) {
        var oi = parseInt(e.target.dataset.optIndex);
        var val = e.target.dataset.optValue;
        selectedOptions[oi] = val;
        // Update active state
        e.target.closest('.adrs-qv-opt-btns').querySelectorAll('.adrs-qv-opt-btn').forEach(function(b) {
          b.classList.toggle('adrs-qv-opt-active', b.dataset.optValue === val);
        });
        updateSelectedVariant();
      }
      // Qty buttons
      if (e.target.id === 'adrs-qv-minus') {
        var qtyEl = document.getElementById('adrs-qv-qty');
        qtyEl.value = Math.max(1, parseInt(qtyEl.value) - 1);
      }
      if (e.target.id === 'adrs-qv-plus') {
        var qtyEl2 = document.getElementById('adrs-qv-qty');
        qtyEl2.value = Math.min(99, parseInt(qtyEl2.value) + 1);
      }
      // Add to cart
      if (e.target.id === 'adrs-qv-atc') {
        var vid = e.target.dataset.variantId;
        var qty = parseInt(document.getElementById('adrs-qv-qty').value) || 1;
        e.target.textContent = 'Adding...';
        e.target.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: qty })
        })
        .then(function(r) { return r.json(); })
        .then(function() {
          e.target.textContent = 'Added!';
          setTimeout(function() {
            closeQuickView();
            // Trigger Dawn's cart drawer if available
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            var cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.open) cartDrawer.open();
          }, 600);
        })
        .catch(function() {
          e.target.textContent = 'Error — try again';
          e.target.disabled = false;
        });
      }
    });
  }

  function formatMoney(cents) {
    var sym = (cfg.currency === 'USD') ? '$' : (cfg.moneyFormat || '').replace(/\{\{.*?\}\}/g, '').trim() || '$';
    return sym + (cents / 100).toFixed(2);
  }

  // Delegate click on quick view buttons (works for dynamically rendered cards)
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.adrs-quickview-btn');
    if (btn && !btn.disabled) {
      e.preventDefault();
      openQuickView(btn.dataset.handle);
    }
  });

})();